/* src/components/ui/CommentModal.js */
import React, { useState, useEffect } from 'react';
import TagInput from '../journal/TagInput'; // Assuming TagInput is in ../journal/

// Reusable Modal for adding comments and tags
const CommentModal = ({
    isOpen,
    onClose,
    onSave,
    title = "Add Journal Note & Tags (Optional)", // Default title
    initialComment = '',
    availableTags = [],
}) => {
    const [comment, setComment] = useState(initialComment);
    const [tags, setTags] = useState([]);
    const [isSaving, setIsSaving] = useState(false);

    // Effect to update local state when modal opens or initial data changes
    useEffect(() => {
        if (isOpen) {
            setComment(initialComment);
            setTags([]); // Reset tags when modal opens
        }
    }, [initialComment, isOpen]);

    // Handler for TagInput changes
    const handleTagsChange = (newTags) => {
        setTags(newTags);
    };

    const handleSave = async () => {
        setIsSaving(true);
        try {
            // Pass both comment and tags back to the parent's onSave
            await onSave(comment, tags); // Parent component handles the actual saving logic
        } catch (error) {
            console.error("Error during modal save operation:", error);
            // Optionally show an error message within the modal or rely on parent
        } finally {
            setIsSaving(false); // Reset saving state regardless of outcome
        }
    };

    if (!isOpen) return null;

    return (
        // Modal Overlay
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-30 p-4 animate-fadeIn">
            {/* Modal Content Box */}
            <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-md transform transition-all animate-scaleIn">
                <h3 className="text-lg font-medium mb-4 text-gray-800">{title}</h3>
                {/* Comment Textarea */}
                <div className="mb-4">
                    <label htmlFor="journalComment" className="sr-only">Journal Comment</label>
                    <textarea
                        id="journalComment"
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                        placeholder="Add any observations or thoughts..."
                        rows="3"
                        className="w-full p-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                        aria-label="Journal comment"
                    />
                </div>

                {/* Tags Input Section */}
                <div className="mb-6">
                    <label htmlFor="journalTags" className="block text-sm font-medium text-gray-700 mb-1">Tags</label>
                    <TagInput
                        id="journalTags"
                        tags={tags}
                        availableTags={availableTags}
                        onChange={handleTagsChange}
                    />
                    <p className="text-xs text-gray-500 mt-1">Enter tags separated by comma or Enter. Max 25 chars, starts with #.</p>
                </div>

                {/* Action Buttons */}
                <div className="flex justify-end space-x-3">
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={isSaving}
                        className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 disabled:opacity-50 transition-colors"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={handleSave}
                        disabled={isSaving} // Disable only if saving
                        className="px-4 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700 disabled:opacity-50 transition-colors"
                    >
                        {isSaving ? 'Saving...' : 'Save to Journal'}
                    </button>
                </div>
            </div>
            {/* Simple animation styles */}
            <style jsx global>{`
                @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
                .animate-fadeIn { animation: fadeIn 0.2s ease-out forwards; }
                @keyframes scaleIn { from { transform: scale(0.95); opacity: 0; } to { transform: scale(1); opacity: 1; } }
                .animate-scaleIn { animation: scaleIn 0.2s ease-out forwards; }
            `}</style>
        </div>
    );
};

export default CommentModal;